import React from 'react';
import {
  X,
  Shield,
  ShieldCheck,
  Fingerprint,
  Smartphone,
  Trash2,
  Plus,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Eye,
  EyeOff,
  LogOut,
  Star,
} from 'lucide-react';
import { FEATURE_LITIGATION } from '../../../constants';
import { PORTAL_SHELL } from '../portalTheme';

export interface PasskeyInfo {
  id: string;
  deviceName: string;
  created: string;
}

export interface SettingsPanelProps {
  onClose: () => void;
  onLogout: () => void;
  isPremium: boolean;
  supportsPasskeys: boolean;
  passkeys: PasskeyInfo[];
  pkLoading: boolean;
  pkRegistering: boolean;
  pkDeviceName: string;
  onPkDeviceNameChange: (v: string) => void;
  pkError: string;
  pkSuccess: string;
  onAddPasskey: () => void;
  onDeletePasskey: (id: string) => void;
  pwOld: string;
  pwNew: string;
  pwConfirm: string;
  onPwOldChange: (v: string) => void;
  onPwNewChange: (v: string) => void;
  onPwConfirmChange: (v: string) => void;
  showPwOld: boolean;
  showPwNew: boolean;
  showPwConfirm: boolean;
  onToggleShowPwOld: () => void;
  onToggleShowPwNew: () => void;
  onToggleShowPwConfirm: () => void;
  pwError: string;
  pwSuccess: boolean;
  pwLoading: boolean;
  onPasswordChange: (e: React.FormEvent) => void;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  onClose,
  onLogout,
  isPremium,
  supportsPasskeys,
  passkeys,
  pkLoading,
  pkRegistering,
  pkDeviceName,
  onPkDeviceNameChange,
  pkError,
  pkSuccess,
  onAddPasskey,
  onDeletePasskey,
  pwOld,
  pwNew,
  pwConfirm,
  onPwOldChange,
  onPwNewChange,
  onPwConfirmChange,
  showPwOld,
  showPwNew,
  showPwConfirm,
  onToggleShowPwOld,
  onToggleShowPwNew,
  onToggleShowPwConfirm,
  pwError,
  pwSuccess,
  pwLoading,
  onPasswordChange,
}) => (
  <>
    <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] flex-shrink-0">
      <h2 className="text-white font-semibold">Einstellungen</h2>
      <button
        type="button"
        onClick={onClose}
        className="p-2 text-slate-500 hover:text-white rounded-lg hover:bg-white/[0.06]"
        aria-label="Schließen"
      >
        <X className="w-5 h-5" />
      </button>
    </div>

    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
      {!isPremium && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
          <p className="text-amber-400 text-sm font-medium flex items-center gap-2">
            <Star className="w-4 h-4" /> Premium
          </p>
          <p className="text-slate-400 text-xs mt-2 leading-relaxed">
            {FEATURE_LITIGATION}, Vertrags-QA und Dokument-Editor — Closed Beta auf Anfrage.
          </p>
          <a
            href="mailto:office@arkorbit.at?subject=Premium Upgrade"
            className="mt-3 block text-center text-xs bg-amber-500/90 hover:bg-amber-400 text-black font-semibold py-2 rounded-lg"
          >
            Upgrade anfragen
          </a>
        </div>
      )}

      {supportsPasskeys && (
        <section className={`${PORTAL_SHELL.ingestBar} p-4 space-y-4`}>
          <div className="flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-blue-400" />
            <h3 className="text-white text-sm font-medium">Passkeys</h3>
          </div>

          {pkLoading ? (
            <p className="text-slate-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Laden…
            </p>
          ) : passkeys.length > 0 ? (
            <ul className="space-y-2">
              {passkeys.map((pk) => (
                <li
                  key={pk.id}
                  className="flex items-center justify-between bg-white/[0.03] rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Smartphone className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{pk.deviceName}</p>
                      <p className="text-[11px] text-slate-600">
                        {new Date(pk.created).toLocaleDateString('de-AT')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeletePasskey(pk.id)}
                    className="text-slate-600 hover:text-red-400 p-1"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500 text-sm">Noch kein Passkey.</p>
          )}

          <input
            type="text"
            value={pkDeviceName}
            onChange={(e) => onPkDeviceNameChange(e.target.value)}
            placeholder="Gerätename (optional)"
            className={PORTAL_SHELL.input + ' px-3 py-2'}
          />
          <button
            type="button"
            onClick={onAddPasskey}
            disabled={pkRegistering}
            className="w-full border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/15 text-blue-300 text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-2"
          >
            {pkRegistering ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Einrichten…
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Passkey hinzufügen
              </>
            )}
          </button>

          {pkError && (
            <p className="text-red-400 text-xs flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> {pkError}
            </p>
          )}
          {pkSuccess && (
            <p className="text-emerald-400 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> {pkSuccess}
            </p>
          )}

          <p className="text-slate-600 text-[11px] flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500/80 flex-shrink-0 mt-0.5" />
            Biometrie verlässt Ihr Gerät nicht.
          </p>
        </section>
      )}

      <section>
        <h3 className="text-white text-sm font-medium flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-slate-500" /> Passwort
        </h3>
        <form onSubmit={onPasswordChange} className={`${PORTAL_SHELL.ingestBar} p-4 space-y-3`}>
          {[
            {
              label: 'Aktuell',
              val: pwOld,
              set: onPwOldChange,
              show: showPwOld,
              toggle: onToggleShowPwOld,
              auto: 'current-password',
            },
            {
              label: 'Neu',
              val: pwNew,
              set: onPwNewChange,
              show: showPwNew,
              toggle: onToggleShowPwNew,
              auto: 'new-password',
            },
            {
              label: 'Bestätigen',
              val: pwConfirm,
              set: onPwConfirmChange,
              show: showPwConfirm,
              toggle: onToggleShowPwConfirm,
              auto: 'new-password',
            },
          ].map(({ label, val, set, show, toggle, auto }) => (
            <div key={label}>
              <label className="text-[11px] text-slate-500 uppercase tracking-wider mb-1 block">
                {label}
              </label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={val}
                  onChange={(e) => set(e.target.value)}
                  autoComplete={auto}
                  className={PORTAL_SHELL.input + ' pl-3 pr-10 py-2.5'}
                />
                <button
                  type="button"
                  onClick={toggle}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white p-1"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}

          {pwError && (
            <p className="text-red-400 text-xs flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> {pwError}
            </p>
          )}
          {pwSuccess && (
            <p className="text-emerald-400 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Passwort gespeichert.
            </p>
          )}

          <button
            type="submit"
            disabled={pwLoading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
          >
            {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
          </button>
        </form>
      </section>

      <div className="flex items-start gap-2 text-[11px] text-slate-600">
        <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        100&nbsp;% DSGVO · Server Frankfurt
      </div>
    </div>

    <div className="px-5 py-4 border-t border-white/[0.06] flex-shrink-0">
      <button
        type="button"
        onClick={onLogout}
        className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors"
      >
        <LogOut className="w-4 h-4" /> Abmelden
      </button>
    </div>
  </>
);
